import { useDestroyFormMutation, useExportFormsMutation, useGetFormsQuery } from "api/TenantSlug/Form/hooks";
import { FormDesignAddDrawer } from "components/Drawers/FormDesignAdd/FormDesignAddDrawer";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell.tsx";
import { LabelsCell } from "components/UI/Table/UI/LabelsCell/LabelsCell.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import FormData = App.Data.FormData;
import GetFormsResponse = App.Http.Responses.Form.GetFormsResponse;
import GetFormResponse = App.Http.Responses.Form.GetFormResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

export const FormDesignTable = () => {
    const tableId = TableId.FormDesignTable;
    const tableAtomInstance = tableAtom<FormData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const destroyFormMutation = useDestroyFormMutation();
    const exportState = useRecoilValue(exportAtom(tableId));
    const getFormsExportMutation = useExportFormsMutation();
    const { t: tG } = usePaldockTranslation("tables.general-columns");

    const request = {
        filter: {
            filter: tableState.filter?.filter || {},
            sort: tableState?.filter?.sort ?? "name",
            include: "formStructure,category,labels",
        },
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
    };

    const fetchFormStructuresQuery = useGetFormsQuery(request, { enabled: false });

    const tableFetchDataUtils = useTableDataApiOperations<
        GetFormsResponse,
        GetFormResponse,
        FormData,
        BaseExportRequest
    >(tableId, fetchFormStructuresQuery, destroyFormMutation, undefined, getFormsExportMutation);

    const columnConfig: TableColumnType<FormData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "name",
            key: "name",
            inputType: "text",
            editable: true,
            sorter: true,
            defaultSorter: true,
            ellipsis: true,
            width: 150,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("structure")} />,
            dataIndex: "formStructure.name",
            key: "formStructure.name",
            render: (_, record) => <EntityLinkCell.FormStructure entity={record.formStructure ?? undefined} />,
            sorter: true,
            ellipsis: true,
            width: 150,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("category")} />,
            dataIndex: "category.name",
            key: "category.name",
            render: (_, data: FormData) => data.category?.name ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.categorySelect,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("tags")} />,
            dataIndex: "labels.name",
            key: "labels.name",
            render: (_, data) => <LabelsCell labels={data.labels} />,
            filterable: true,
            filterType: FILTER_TYPES.labelMultiSelect,
            ellipsis: false,
            width: 150,
        },
        /*{
            title: <ColumnHeaderTitle columnTitle={t("columns.date")} />,
            dataIndex: "date",
            key: "date",
            inputType: "date",
            editable: true,
        },*/
        {
            title: <ColumnHeaderTitle columnTitle={tG("id")} />,
            dataIndex: "id",
            key: "id",
            inputType: "text",
            sorter: true,
            ellipsis: true,
            width: 150,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        /* {
            title: (
                <ColumnHeaderTitle columnTitle={tG("description")} />
            ),
            dataIndex: "description",
            key: "description",
            inputType: "text",
        },*/
        /*{
            title: <ColumnHeaderTitle columnTitle={t("columns.category")} />,
            dataIndex: "category",
            key: "category",
            inputType: "text",
            editable: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.country")} />,
            dataIndex: "country",
            key: "country",
            inputType: "text",
            editable: true,
            render: (country: CountryData) => <CountryCell country={country} />,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.category")} />,
            dataIndex: "category",
            key: "category",
            inputType: "text",
            editable: true,
        },*/
    ];

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<FormData>({
        entityName: "form",
        editPermission: PermissionEnum.ToolDesignFormEditor,
        detailPermission: PermissionEnum.ToolDesignFormGeneral,
        editUrl: "/$tenantSlug/tool/design/form/:id/general",
    });

    return (
        <>
            <Table<GetFormsResponse, FormData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetFormsResponse}
                dataSource={tableFetchDataUtils.tableData?.forms || []}
                columnConfig={columnConfig}
                loading={fetchFormStructuresQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                defaultDelete={tableFetchDataUtils.deleteTableData}
                addEntityData={{ drawerId: DrawerId.FormDesignAdd, permission: PermissionEnum.FormCreate }}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
                hideActionsColumn={true}
                enableFilters={true}
                autoScrollWidth={true}
            />
            <FormDesignAddDrawer onSubmit={() => tableFetchDataUtils.refetchData()} />
        </>
    );
};
